<template>
  <div id="usersList">
    <card :title="$t('a-users:filter')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-md-4">
            <label for="filterLogin">{{ $t('a-users:login') | capitalize }}</label>
            <input id="filterLogin"
                   v-model="filterLogin"
                   v-validate="'text'"
                   :data-vv-as="$t('a-users:login')"
                   :class="{ 'is-invalid': errors.has('filterLogin') }"
                   type="text"
                   name="filterLogin"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('filterLogin') }}</div>
          </div>

          <div class="form-group col-md-4">
            <label for="filterEmail">{{ $t('a-users:email') | capitalize }}</label>
            <input id="filterEmail"
                   v-model="filterEmail"
                   v-validate="'text'"
                   :data-vv-as="$t('a-users:email')"
                   :class="{ 'is-invalid': errors.has('email') }"
                   type="text"
                   name="filterEmail"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('email') }}</div>
          </div>

          <div class="form-group col-md-4">
            <label for="filterRole">{{ $t('a-users:role') | capitalize }}</label>
            <select id="filterRole" v-model="filterRole" name="filterRole" class="form-control">
              <option value="" />
              <option value="READER">READER</option>
              <option value="USER">USER</option>
              <option value="ADMIN">ADMIN</option>
              <option value="SUPER_ADMIN">SUPER_ADMIN</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div v-if="isRole.superAdmin" class="form-group col-md-6">
            <label for="filterCustomer">{{ $t('a-users:customer') | capitalize }}</label>
            <select id="filterCustomer" v-model="filterCustomer" name="filterCustomer" class="form-control">
              <option value="" />
              <option v-for="(c, indexCust) in $store.getters.customers" :key="indexCust" :value="c">{{ c.name }}</option>
            </select>
          </div>

          <div class="form-group col-md-3">
            <label for="filterDisabled">{{ $t('a-users:activation') | capitalize }}</label>
            <select id="filterDisabled" v-model="filterDisabled" name="filterDisabled" class="form-control">
              <option value="" />
              <option value="true">{{ $t('a-users:enabled') | capitalize }}</option>
              <option value="false">{{ $t('a-users:disabled') | capitalize }}</option>
            </select>
          </div>

          <div class="form-group col-md-3">
            <label for="filterWithExpirationDate">{{ $t('a-users:expiration') | capitalize }}</label>
            <select id="filterWithExpirationDate" v-model="filterWithExpirationDate" name="filterWithExpirationDate" class="form-control">
              <option value="" />
              <option value="true">{{ $t('a-users:withExpirationDate') | capitalize }}</option>
              <option value="false">{{ $t('a-users:withoutExpirationDate') | capitalize }}</option>
            </select>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('a-users:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="adminUsersGrid"
          :title="$t('a-users:users')"
          :columns="columns"
          :paginated-dto="users"
          @load="filter"
    >
      <template #grid-menu>
        <b-dropdown-item @click.prevent="sendEmailTo">{{ $t('a-users:sendEmail') | capitalize }}</b-dropdown-item>
      </template>

      <div slot="login" slot-scope="props" :data-user-id="props.dto.id" :data-user-login="props.dto.login" class="d-flex align-items-center justify-content-between">
        <span class="d-flex align-items-center">
          <avatar :name="props.dto.login" :picture-url="props.dto.avatar" class="mr-2" size="sm" />
          {{ props.dto.login }}
        </span>
        <span style="flex: 1;" />
        <i v-if="!props.dto.enabled" :title="$t('a-users:disabled') | capitalize" class="icon-user-block text-danger no-pointer mr-2" />
        <i v-if="!props.dto.validated" :title="$t('a-users:notValidated') | capitalize" class="icon-user-lock text-muted no-pointer" />
      </div>

      <div slot="actions" slot-scope="props" :data-user-id="props.dto.id" class="text-right">
        <i v-if="!props.dto.validated" :id="'usersList-sendValidationEmail-' + props.dto.id" class="icon-mail" @click="sendValidationEmail(props.dto)" />
        <i v-if="props.dto.customer.id" :id="'usersList-editUser-' + props.dto.id" class="icon-edit" @click="editUser(props.dto)" />
        <i v-if="canDeleteUser(props.dto)" :id="'usersList-deleteUser-' + props.dto.id" class="icon-trash" @click="confirmDeleteUser(props.dto)" />
      </div>
    </grid>

    <confirm id="deleteUser"
             v-model="showDeleteUserConfirm"
             :message="$t('a-users:confirmDelete', { login: userToDelete.login })"
             @confirm:validate="deleteUser"
    />

    <floating-button :click-handler="() => { addUser=true }" icon-classes="icon-add" />
    <users-form v-model="addUser" @success="filter" />
  </div>
</template>

<script>
import { find, map } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

import BDropdownItem from 'bootstrap-vue/es/components/dropdown/dropdown-item'
import UsersForm from './UsersForm'

export default {
  titleKey: 'administrationUsers',

  components: { BDropdownItem, UsersForm },

  data () {
    return {
      // filters
      filterLogin: null,
      filterEmail: null,
      filterCustomer: null,
      filterRole: null,
      filterDisabled: null,
      filterWithExpirationDate: null,
      // List
      users: {},
      showDeleteUserConfirm: false,
      userToDelete: {},
      // Add user
      addUser: false
    }
  },

  computed: {
    columns () {
      let columns = [
        { text: this.$t('a-users:login'), slot: 'login', sort: 'login' },
        { text: this.$t('a-users:email'), id: 'email' }
      ]

      if (this.isRole.superAdmin) {
        columns.push({ text: this.$t('a-users:customer'), formatter: user => user.customer.name })
      }

      columns.push({ text: this.$t('a-users:role'), formatter: user => user.role })
      columns.push({ slot: 'actions' })

      return columns
    }
  },

  created () {
    this.filter()

    if (this.isRole.superAdmin) this.$store.dispatch('getCustomers')
  },

  methods: {
    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let search = []

      // Add criteria
      if (this.filterLogin) search = this.$utils.addSearch(search, 'like', '*' + this.filterLogin + '*', 'login')
      if (this.filterEmail) search = this.$utils.addSearch(search, 'like', '*' + this.filterEmail + '*', 'email')
      if (this.filterCustomer) search = this.$utils.addSearch(search, 'eq', this.filterCustomer.id, 'customer.id')
      if (this.filterRole) search = this.$utils.addSearch(search, 'eq', this.filterRole, 'role')
      if (this.filterDisabled) search = this.$utils.addSearch(search, 'eq', this.filterDisabled, 'enabled')
      if (this.filterWithExpirationDate) search = this.$utils.addSearch(search, this.filterWithExpirationDate === 'true' ? 'ne' : 'eq', null, 'expirationDate')

      // Format criteria
      if (search.length > 0) options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      // Get filtered data
      this.getUsers(options)
    },

    async getUsers (options) {
      const params = this.$utils.getParams({ sort: '+login' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getUsers(this.$store.getters.application, params))
      if (error || !response) return

      this.users = response.data
    },

    async sendEmailTo (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let search = []
      let searchString = ''

      // Add criteria
      if (this.filterLogin) search = this.$utils.addSearch(search, 'like', '*' + this.filterLogin + '*', 'login')
      if (this.filterEmail) search = this.$utils.addSearch(search, 'like', '*' + this.filterEmail + '*', 'email')
      if (this.filterCustomer) search = this.$utils.addSearch(search, 'eq', this.filterCustomer.id, 'customer.id')
      if (this.filterRole) search = this.$utils.addSearch(search, 'eq', this.filterRole, 'role')
      if (this.filterDisabled) search = this.$utils.addSearch(search, 'eq', this.filterDisabled, 'enabled')
      if (this.filterWithExpirationDate) search = this.$utils.addSearch(search, this.filterWithExpirationDate === 'true' ? 'ne' : 'eq', null, 'expirationDate')
      // Only validated users
      search = this.$utils.addSearch(search, 'eq', true, 'validated')

      // Format criteria
      if (search.length > 0) searchString = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      // Get filtered data
      let error, response
      [error, response] = await to(this.$api.getAllUsers(this.$store.getters.application, { search: searchString }))
      if (error || !response) return

      const emailsList = map(response.data.list, 'email').join(';')
      window.location.href = 'mailto:' + emailsList
    },

    async sendValidationEmail (user = {}) {
      let error
      [error] = await to(this.$api.sendValidationEmail(user))
      if (error) eventBus.$emit('message-error', this.$t('a-users:sendValidationEmailError', { email: user.email }))
      else eventBus.$emit('message-success', this.$t('a-users:sendValidationEmailSuccess', { email: user.email }))
    },

    // Can delete user:
    // - if user role <= authenticated user role
    // - if user != authenticated user
    canDeleteUser (user) {
      const authenticatedUser = this.$store.getters.authenticatedUser
      const authenticatedUserRole = find(this.$store.getters.roles, role => role.name === authenticatedUser.role) || 0
      const userRole = find(this.$store.getters.roles, role => role.name === user.role) || 0

      return user.id !== authenticatedUser.id && userRole.level <= authenticatedUserRole.level
    },

    confirmDeleteUser (user) {
      this.userToDelete = user
      this.showDeleteUserConfirm = true
    },

    editUser (user) {
      this.$router.push('/profile/' + user.id)
    },

    async deleteUser () {
      let error
      [error] = await to(this.$api.deleteUser(this.userToDelete))
      if (error) return

      eventBus.$emit('message-success', this.$t('a-users:deleteSuccess', { login: this.userToDelete.login }))
      this.filter()
    }
  }
}
</script>

<style lang="scss">
  #adminUsersGrid tr > td {
    vertical-align: middle;
  }
</style>
